import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { vehicleMakes, getModelsForMake } from "../vehicleData";
import {
  Upload,
  X,
  CheckCircle,
  Loader2,
  Phone,
  Mail,
  MapPin,
  Clock,
  Star,
  Facebook,
  Instagram,
  Twitter,
} from "lucide-react";

const Footer = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedMake, setSelectedMake] = useState("");
  const [availableModels, setAvailableModels] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    vehicleMake: "",
    vehicleModel: "",
    vin: "",
    damageDescription: "",
    images: [],
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleVinChange = (e) => {
    // VINs are uppercase and never contain the letters I, O or Q
    const vin = e.target.value
      .toUpperCase()
      .replace(/[^A-HJ-NPR-Z0-9]/g, "")
      .slice(0, 17);
    setFormData((prev) => ({
      ...prev,
      vin,
    }));
  };

  const handleMakeChange = (value) => {
    setSelectedMake(value);
    const models = getModelsForMake(value);
    setAvailableModels(models);
    setFormData((prev) => ({
      ...prev,
      vehicleMake: value,
      vehicleModel: "", // Reset model when make changes
    }));
  };

  const handleModelChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      vehicleModel: value,
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const newImages = files.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
        name: file.name,
      }));

      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...newImages].slice(0, 3), // Limit to 3 images for footer
      }));
    }
  };

  const removeImage = (index) => {
    setFormData((prev) => {
      // Clean up preview URL to prevent memory leaks
      if (prev.images[index]?.preview) {
        URL.revokeObjectURL(prev.images[index].preview);
      }

      return {
        ...prev,
        images: prev.images.filter((_, i) => i !== index),
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Convert images to base64
      const imagePromises = formData.images.map(async (img) => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve({
              name: img.name,
              data: reader.result,
            });
          };
          reader.readAsDataURL(img.file);
        });
      });

      const imagesBase64 = await Promise.all(imagePromises);

      // Send form data to PHP backend
      const response = await fetch("/contact-form.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          vehicleMake: formData.vehicleMake,
          vehicleModel: formData.vehicleModel,
          vin: formData.vin,
          damageDescription: formData.damageDescription,
          images: imagesBase64,
        }),
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Quote Request Submitted Successfully! 🏁",
          description: result.message,
        });
      } else {
        throw new Error(result.message);
      }

      // Clean up preview URLs
      formData.images.forEach((imageObj) => {
        if (imageObj.preview) {
          URL.revokeObjectURL(imageObj.preview);
        }
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        vehicleMake: "",
        vehicleModel: "",
        vin: "",
        damageDescription: "",
        images: [],
      });
      setSelectedMake("");
      setAvailableModels([]);

      // Reset file input
      const fileInput = document.getElementById("footer-image-upload");
      if (fileInput) fileInput.value = "";
    } catch (error) {
      toast({
        title: "Submission Error",
        description:
          error.message || "Please call (410) 292-5300 for immediate service.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      {/* Quick Quote Form Section */}
      <section className="py-16 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-yellow-400 mb-4">
              Get Your Quick Quote
            </h2>
            <p className="text-xl text-gray-300">
              Fast, easy, and free estimates. Upload photos and we'll get back
              to you within 24 hours!
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-8">
              {/* Left Column - Contact Info */}
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Your Information
                </h3>

                <div>
                  <Label
                    htmlFor="footer-name"
                    className="text-gray-300 font-semibold"
                  >
                    Full Name *
                  </Label>
                  <Input
                    id="footer-name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    required
                    className="mt-2 bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-yellow-500"
                  />
                </div>

                <div>
                  <Label
                    htmlFor="footer-email"
                    className="text-gray-300 font-semibold"
                  >
                    Email Address *
                  </Label>
                  <Input
                    id="footer-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your.email@example.com"
                    required
                    className="mt-2 bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-yellow-500"
                  />
                </div>

                <div>
                  <Label
                    htmlFor="footer-phone"
                    className="text-gray-300 font-semibold"
                  >
                    Phone Number *
                  </Label>
                  <Input
                    id="footer-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(555) 123-4567"
                    required
                    className="mt-2 bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-yellow-500"
                  />
                </div>

                <div>
                  <Label className="text-gray-300 font-semibold">
                    Vehicle Make *
                  </Label>
                  <Select onValueChange={handleMakeChange} value={selectedMake}>
                    <SelectTrigger
                      data-testid="footer-vehicle-make-select"
                      className="mt-2 bg-gray-800 border-gray-600 text-white focus:border-yellow-500"
                    >
                      <SelectValue placeholder="Select vehicle make" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-600 text-white max-h-60">
                      {vehicleMakes.map((make) => (
                        <SelectItem
                          key={make.value}
                          value={make.value}
                          className="hover:bg-gray-700"
                        >
                          {make.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-gray-300 font-semibold">
                    Vehicle Model *
                  </Label>
                  <Select
                    onValueChange={handleModelChange}
                    value={formData.vehicleModel}
                    disabled={!selectedMake}
                  >
                    <SelectTrigger
                      data-testid="footer-vehicle-model-select"
                      className="mt-2 bg-gray-800 border-gray-600 text-white focus:border-yellow-500 disabled:opacity-50"
                    >
                      <SelectValue
                        placeholder={
                          selectedMake
                            ? "Select vehicle model"
                            : "Select make first"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-600 text-white max-h-60">
                      {availableModels.map((model, index) => (
                        <SelectItem
                          key={index}
                          value={model}
                          className="hover:bg-gray-700"
                        >
                          {model}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label
                    htmlFor="footer-vin"
                    className="text-gray-300 font-semibold"
                  >
                    VIN (Optional)
                  </Label>
                  <Input
                    id="footer-vin"
                    name="vin"
                    value={formData.vin}
                    onChange={handleVinChange}
                    placeholder="17-character VIN (e.g. 1HGCM82633A004352)"
                    maxLength={17}
                    minLength={17}
                    pattern="[A-HJ-NPR-Z0-9]{17}"
                    title="A VIN is 17 characters and does not contain the letters I, O or Q"
                    autoComplete="off"
                    spellCheck="false"
                    className="mt-2 bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-yellow-500 font-mono uppercase tracking-wider"
                    data-testid="footer-vin-input"
                  />
                  <p className="text-sm text-gray-400 mt-1">
                    {formData.vin.length > 0 && formData.vin.length < 17
                      ? `${17 - formData.vin.length} character(s) remaining`
                      : "Found on your dashboard, driver's door jamb, or insurance card. Helps us quote exact parts."}
                  </p>
                </div>
              </div>

              {/* Right Column - Damage Description and Images */}
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Damage Details
                </h3>

                <div>
                  <Label
                    htmlFor="footer-damage"
                    className="text-gray-300 font-semibold"
                  >
                    Damage Description *
                  </Label>
                  <Textarea
                    id="footer-damage"
                    name="damageDescription"
                    value={formData.damageDescription}
                    onChange={handleInputChange}
                    placeholder="Please describe the damage to your vehicle..."
                    required
                    className="mt-2 h-24 bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-yellow-500"
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <Label className="text-gray-300 font-semibold">
                    Upload Damage Photos
                  </Label>
                  <p className="text-sm text-gray-400 mb-3">
                    Upload up to 3 photos (JPG, PNG, max 10MB each)
                  </p>

                  <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center hover:border-yellow-500 transition-colors bg-gray-800">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="footer-image-upload"
                    />
                    <label
                      htmlFor="footer-image-upload"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      <Upload className="h-8 w-8 text-yellow-400 mb-3" />
                      <p className="text-yellow-400 font-semibold mb-1">
                        Click to upload photos
                      </p>
                      <p className="text-xs text-gray-400">
                        or drag and drop images here
                      </p>
                    </label>
                  </div>

                  {/* Image Preview */}
                  {formData.images.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-300 mb-3">
                        {formData.images.length} image(s) selected:
                      </p>
                      <div className="grid grid-cols-3 gap-3">
                        {formData.images.map((image, index) => (
                          <div key={index} className="relative">
                            <img
                              src={image.preview}
                              alt={`Upload ${index + 1}`}
                              className="w-full h-16 object-cover rounded border border-gray-600"
                            />
                            <Button
                              type="button"
                              size="icon"
                              variant="destructive"
                              className="absolute -top-2 -right-2 h-5 w-5 rounded-full"
                              onClick={() => removeImage(index)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                            <p className="text-xs text-gray-400 mt-1 truncate">
                              {image.name}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-yellow-500 hover:bg-yellow-400 text-black py-4 text-lg font-bold mt-6"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="animate-spin h-5 w-5 mr-2" />
                      Submitting Quote Request...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-5 w-5" />
                      Get My Free Quote
                    </>
                  )}
                </Button>

                <p className="text-xs text-gray-400 text-center mt-3">
                  By submitting this form, you agree to be contacted by WOW
                  AUTOBODY regarding your quote request.
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Footer Info Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="flex items-center mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_car-fix-portfolio/artifacts/9nlfi0rb_Wow-Body-Logo.png"
                  alt="WOW AUTOBODY Logo"
                  className="h-12 w-auto mr-4"
                />
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Professional collision repair, dent removal, and auto painting
                services. We restore your vehicle to its original beauty with
                precision and care. Serving the community since 2010 with ASE
                certified technicians.
              </p>
              <div className="flex space-x-4">
                <Button
                  size="icon"
                  variant="ghost"
                  className="text-gray-400 hover:text-yellow-400"
                >
                  <Facebook className="h-5 w-5" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="text-gray-400 hover:text-yellow-400"
                >
                  <Instagram className="h-5 w-5" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="text-gray-400 hover:text-yellow-400"
                >
                  <Twitter className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-xl font-bold text-yellow-400 mb-4">
                Contact Info
              </h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <Phone className="h-5 w-5 text-yellow-400 mr-3" />
                  <span className="text-gray-300">
                    <a href="call:4102925300">
                      (410) 292-5300 (English/Spanish)
                    </a>
                  </span>
                </div>
                <div className="flex items-center">
                  <Phone className="h-5 w-5 text-yellow-400 mr-3" />
                  <span className="text-gray-300">
                    <a href="call:4109804149">(410) 980-4149 (Korean)</a>
                  </span>
                </div>
                <div className="flex items-center">
                  <Mail className="h-5 w-5 text-yellow-400 mr-3" />
                  <span className="text-gray-300">
                    <a href="mailto:info@wowautobody.com">
                      info@wowautobody.com
                    </a>
                  </span>
                </div>
                <div className="flex items-start">
                  <MapPin className="h-5 w-5 text-yellow-400 mr-3 mt-1" />
                  <span className="text-gray-300">
                    <a
                      href="https://www.google.com/maps/place/WOW+AUTO+BODY+Collision+Center./@39.2835455,-76.7622896,17z/data=!3m1!4b1!4m6!3m5!1s0x89c81ebe3fce9b2b:0xd63cbcdc9b235843!8m2!3d39.2835414!4d-76.7597147!16s%2Fg%2F11ggs51gc0?entry=ttu&g_ep=EgoyMDI2MDEyOC4wIKXMDSoASAFQAw%3D%3D"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      6427 Balitmore National Pike
                      <br />
                      Cantonsville, MD 21228
                    </a>
                  </span>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div>
              <h3 className="text-xl font-bold text-yellow-400 mb-4">
                Business Hours
              </h3>
              <div className="space-y-2">
                <div className="flex items-center">
                  <Clock className="h-4 w-4 text-yellow-400 mr-3" />
                  <div className="text-gray-300 text-sm">
                    <p className="font-semibold">Monday - Friday</p>
                    <p>9:00 AM - 5:00 PM</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 text-yellow-400 mr-3" />
                  <div className="text-gray-300 text-sm">
                    <p className="font-semibold">Saturday</p>
                    <p>Closed</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 text-yellow-400 mr-3" />
                  <div className="text-gray-300 text-sm">
                    <p className="font-semibold">Sunday</p>
                    <p>Closed</p>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <div className="flex items-center mb-2">
                  <Star className="h-4 w-4 text-yellow-400 mr-2" />
                  <span className="text-yellow-400 font-semibold">
                    Emergency Services Available
                  </span>
                </div>
                <p className="text-gray-400 text-sm">
                  Call anytime for emergency collision repairs
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom Copyright */}
      <section className="bg-black py-4 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2026 WOW AUTOBODY. All rights reserved. Licensed & Insured.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a
                href="#"
                className="text-gray-400 hover:text-yellow-400 text-sm transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="#"
                className="text-gray-400 hover:text-yellow-400 text-sm transition-colors"
              >
                Terms of Service
              </a>
              <a
                href="#"
                className="text-gray-400 hover:text-yellow-400 text-sm transition-colors"
              >
                Warranty
              </a>
            </div>
          </div>
        </div>
      </section>
    </footer>
  );
};

export default Footer;
